import React, { useEffect, useState, useRef } from "react";
import { Button, Row, Modal, Form, Card, InputGroup } from "react-bootstrap";
import axios from "axios";
import { jsPDF } from 'jspdf';
import { useNotification } from "../../context/NotificationContext";
import SurveyList from "./feedback-components/SurveyList";
import SurveyEditor from "./feedback-components/SurveyEditor";
import templates from "./FeedbackTemplates"; // Adjust the path as needed
import { generateSurveyLink } from "./feedback-components/config";

const ManageSurveys = () => {
  const [surveys, setSurveys] = useState([]);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [questions, setQuestions] = useState([]);
  // Branding (without conditionalRedirect)
  const [branding, setBranding] = useState({
    themeColor: "#ff4100",
    titleColor: "#000000",
    titleFontSize: 2,
    showLogo: false,
    logoSrc: "",
    buttonStyle: "rounded",
    background: "solid",
    backgroundValue: "#ffffff",
    thankYou: "Thank you for your feedback!",
    redirectUrl: "", // keep plain redirect if needed
  });
  const [settings, setSettings] = useState({
    anonymous: true,
    expiry: "",
    notifications: false,
    recipients: "",
    access: "public",
    language: "en",
  });
  const [globalSettings, setGlobalSettings] = useState({
    holder_logo: "",
    holder_caption: "",
    holder_desc: "",
    form_caption: "",
    form_desc: "",
    show_logo: true,
    show_phone: true,
    show_email: true,
    show_birthday: true,
    show_anniversary: true,
    show_suggestion: true,
    overall_exp: true,
    form_title: "",
    form_desc_long: "",
    thank_you_msg: "",
  });
  const [qrCodeSettings, setQrCodeSettings] = useState({
    enabled: false,
    size: 128,
    fgColor: "#000000",
    bgColor: "#ffffff",
    showLogo: true,
    logoSize: 0.2,
    qrTitle: "",
    qrTitleColor: "#000000",
    qrTitleSize: 24,
    qrDesc: "",
    qrDescColor: "#000000",
    qrDescSize: 16,
    headerLogo: "",
    borderGradientStart: "#007bff",
    borderGradientEnd: "#66b2ff",
    showHolderLogo: false,
    headerLogoWidth: 200,
    headerLogoHeight: 80,
    pageSize: '4-6',

  });
  // Move conditionalRedirect here
  const [conditionalLogic, setConditionalLogic] = useState({
    enabled: false,
    thresholdQuestionId: null,
    threshold: 7,
    goodUrl: "",
    badUrl: "",
    goodMessage: "Thank you! Please rate us here.",
    badMessage: "Thank you for your feedback!",
  });
  const [surveyLink, setSurveyLink] = useState("");
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [activeKey, setActiveKey] = useState("details");
  const initialConfigRef = useRef(null);
  const { notify } = useNotification();
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000/api/pos";
  const token = localStorage.getItem("token");

    const companyLogo = localStorage.getItem("companylogo");
  const cdnUrl = process.env.REACT_APP_CDN_URL;
const logoNetShine = companyLogo && companyLogo.trim() ? `${cdnUrl}/${companyLogo}` : 'https://netshine-assets.blr1.digitaloceanspaces.com/company_87/logos/company_logo_87_1739613280498';
  const defaultLogoSrc = companyLogo && companyLogo.trim() ? `${cdnUrl}/${companyLogo}` : logoNetShine;

  const authHeaders = () => ({
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    },
  });
  useEffect(() => {
    fetchSurveys();
    fetchGlobalSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Fetch surveys
  const fetchSurveys = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/feedback/surveys`, authHeaders());
      const data = Array.isArray(res.data) ? res.data : [];
      const normalized = data.map((s) => ({
        id: s.surveyid || s.id || `temp-${Date.now()}-${Math.random()}`,
        surveyid: s.surveyid || s.id || `temp-${Date.now()}-${Math.random()}`,
        title: s.title || "Untitled Survey",
        key: (s.sharing && s.sharing.key) || s.key || `survey-${s.surveyid || s.id || Date.now()}`,
        createdAt: s.created_at
          ? new Date(s.created_at).toISOString().split("T")[0]
          : s.createdAt || new Date().toISOString().split("T")[0],
        status: s.status || "Draft",
        responses: s.total_responses || s.responses || 0,
        questions: s.questions || [],
        branding: s.branding || {},
        settings: s.settings || {},
        qrCodeSettings: s.qrcode_settings || {},
        conditionalLogic: s.conditional_logic || {},
        description: s.description || "",
      }));
      setSurveys(normalized);
    } catch (err) {
      console.error("Error fetching surveys:", err);
      notify("error", "Failed to load surveys.");
      setSurveys([]);
    } finally {
      setLoading(false);
    }
  };
  const fetchGlobalSettings = async () => {
    try {
      const res = await axios.get(`${apiUrl}/feedback/settings`, authHeaders());
      if (res.data) setGlobalSettings((prev) => ({ ...prev, ...res.data }));
    } catch (err) {
      console.warn("Could not fetch global settings:", err?.response?.status || err);
    }
  };
  // Load a survey for editing
  const loadSurveyFromApi = async (surveyid) => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiUrl}/feedback/surveys/${surveyid}`, authHeaders());
      const s = res.data;
      const normalized = {
        id: s.surveyid || s.id || `temp-${Date.now()}-${Math.random()}`,
        surveyid: s.surveyid || s.id || `temp-${Date.now()}-${Math.random()}`,
        title: s.title || "Untitled Survey",
        key: (s.sharing && s.sharing.key) || s.key || `survey-${s.surveyid || s.id || Date.now()}`,
        createdAt: s.created_at
          ? new Date(s.created_at).toISOString().split("T")[0]
          : s.createdAt || new Date().toISOString().split("T")[0],
        status: s.status || "Draft",
        responses: s._response_count || s.responses || 0,
        questions: s.questions || [],
        branding: s.branding || {},
        settings: s.settings || {},
        qrCodeSettings: s.qrcode_settings || {},
        conditionalLogic: s.conditional_logic || {},
        description: s.description || "",
      };
      setEditingSurvey(normalized);
      setQuestions(normalized.questions || []);
      setBranding({ ...branding, ...(normalized.branding || {}) });
      setSettings({ ...settings, ...(normalized.settings || {}) });
      setQrCodeSettings({ ...qrCodeSettings, ...(normalized.qrCodeSettings || {}) });
      setConditionalLogic({ ...conditionalLogic, ...(normalized.conditionalLogic || {}) });
      updateSharing(normalized);
      initialConfigRef.current = JSON.stringify(normalized);
    } catch (err) {
      console.error("Error loading survey:", err);
      notify("error", "Failed to load survey details.");
    } finally {
      setLoading(false);
    }
  };
  const startEditing = async (survey = null) => {
    if (survey) {
      if (survey.surveyid) {
        await loadSurveyFromApi(survey.surveyid);
      } else {
        setEditingSurvey(survey);
        setQuestions(survey.questions || []);
        setBranding(survey.branding || branding);
        setSettings(survey.settings || settings);
        setQrCodeSettings(survey.qrCodeSettings || qrCodeSettings);
        setConditionalLogic(survey.conditionalLogic || conditionalLogic);
        updateSharing(survey);
        initialConfigRef.current = JSON.stringify(survey);
      }
    } else setShowTemplateModal(true);
    setActiveKey("details");
  };
  const loadTemplate = (template) => {
    const newId = (surveys.length || 0) + 1;
    const newKey = `survey-${newId}`;
    const newSurvey = {
      id: newId,
      title: template.title || "Untitled Survey",
      key: newKey,
      createdAt: new Date().toISOString().split("T")[0],
      status: "Draft",
      responses: 0,
      questions: template.questions || [],
      branding: template.branding || branding,
      settings: template.settings || settings,
      qrCodeSettings: template.qrCodeSettings || qrCodeSettings,
      conditionalLogic: template.conditionalLogic || {},
    };
    setEditingSurvey(newSurvey);
    setQuestions(newSurvey.questions);
    setBranding(newSurvey.branding);
    setSettings(newSurvey.settings);
    setQrCodeSettings(newSurvey.qrCodeSettings);
    setConditionalLogic(newSurvey.conditionalLogic);
    updateSharing(newSurvey);
    setShowTemplateModal(false);
    initialConfigRef.current = JSON.stringify(newSurvey);
  };
  const loadBlank = () => {
    const newId = (surveys.length || 0) + 1;
    const newKey = `survey-${newId}`;
    const newSurvey = {
      id: newId,
      title: "",
      key: newKey,
      createdAt: new Date().toISOString().split("T")[0],
      status: "Draft",
      responses: 0,
      questions: [],
      branding: branding,
      settings: settings,
      qrCodeSettings: qrCodeSettings,
      conditionalLogic: {},
    };
    setEditingSurvey(newSurvey);
    setQuestions(newSurvey.questions);
    setBranding(newSurvey.branding);
    setSettings(newSurvey.settings);
    setQrCodeSettings(newSurvey.qrCodeSettings);
    setConditionalLogic(newSurvey.conditionalLogic);
    updateSharing(newSurvey);
    setShowTemplateModal(false);
    initialConfigRef.current = JSON.stringify(newSurvey);
  };
  const stopEditing = () => {
    setEditingSurvey(null);
    setQuestions([]);
    setIsDownloadOpen(false);
    setActiveKey("details");
  };
  const updateSharing = (survey) => {
    const link = generateSurveyLink(survey.surveyid || survey.id);
    setSurveyLink(link);
  };
  const generateQRCanvas = (callback) => {
    const svg = document.getElementById("qr-code");
    if (!svg) {
      console.error("QR code SVG not found");
      return;
    }
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src =
      "data:image/svg+xml;base64," +
      window.btoa(unescape(encodeURIComponent(svgData)));
    img.onload = () => {
      // Target resolution for 6×6 cm @ 300dpi = ~708px
      const targetPx = Math.round((6 * 300) / 2.54); // cm → px
      const canvas = document.createElement("canvas");
      canvas.width = targetPx;
      canvas.height = targetPx;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      callback(canvas);
    };
    img.onerror = () => {
      console.error("Failed to load QR SVG for rasterization");
    };
  };
  function renderQRWithBorder(
    pdf,
    qrDataURL,
    qrX,
    qrY,
    qrCm,
    options = {}
  ) {
    const {
      borderThicknessCm = 0.5, // thickness in cm
      gradientStart = "#007bff", // dark blue
      gradientEnd = "#66b2ff",   // light blue
    } = options;
    const borderPx = 800; // high-res canvas
    const borderCanvas = document.createElement("canvas");
    borderCanvas.width = borderPx;
    borderCanvas.height = borderPx;
    const ctx = borderCanvas.getContext("2d");
    // Gradient fill
    const gradient = ctx.createLinearGradient(0, 0, borderPx, borderPx);
    gradient.addColorStop(0, gradientStart);
    gradient.addColorStop(1, gradientEnd);
    // Outer rounded rect
    const radius = 60;
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(radius, 0);
    ctx.lineTo(borderPx - radius, 0);
    ctx.quadraticCurveTo(borderPx, 0, borderPx, radius);
    ctx.lineTo(borderPx, borderPx - radius);
    ctx.quadraticCurveTo(borderPx, borderPx, borderPx - radius, borderPx);
    ctx.lineTo(radius, borderPx);
    ctx.quadraticCurveTo(0, borderPx, 0, borderPx - radius);
    ctx.lineTo(0, radius);
    ctx.quadraticCurveTo(0, 0, radius, 0);
    ctx.closePath();
    ctx.fill();
    // White inner box
    const innerMargin = (borderThicknessCm / qrCm) * borderPx;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(
      innerMargin,
      innerMargin,
      borderPx - innerMargin * 2,
      borderPx - innerMargin * 2
    );
    // Convert to image
    const borderDataURL = borderCanvas.toDataURL("image/png");
    // Draw gradient border box
    pdf.addImage(
      borderDataURL,
      "PNG",
      qrX - borderThicknessCm,
      qrY - borderThicknessCm,
      qrCm + borderThicknessCm * 2,
      qrCm + borderThicknessCm * 2
    );
    // Draw QR inside
    pdf.addImage(qrDataURL, "PNG", qrX, qrY, qrCm, qrCm);
  }


const downloadQRPDF = (
  qrTitle,
  qrTitleColor,
  qrTitleSize,
  qrDesc,
  qrDescColor,
  qrDescSize,
  headerLogoWidth,
  headerLogoHeight
) => {
  const pageSize = qrCodeSettings.pageSize || "4-6";
  const [widthIn, heightIn] = pageSize.split("-").map(Number);

  const baseWidthIn = 4;
  const baseHeightIn = 6;
  const scale = Math.min(widthIn / baseWidthIn, heightIn / baseHeightIn);

  const pageW = widthIn * 2.54; // cm
  const pageH = heightIn * 2.54; // cm
  const dpi = 300;
  const pxToCm = 2.54 / 96;

  // Safe margin inside border
  const safeMargin = qrCodeSettings.borderless ? 0 : 0.25; // cm

  // Footer strip height
  const footerH = 2.5 * scale;

  // --- Gradient border background ---
  const borderCanvas = document.createElement("canvas");
  borderCanvas.width = Math.round(widthIn * dpi);
  borderCanvas.height = Math.round(heightIn * dpi);
  const ctx = borderCanvas.getContext("2d");

  const gradient = ctx.createLinearGradient(0, 0, borderCanvas.width, borderCanvas.height);
  gradient.addColorStop(0, qrCodeSettings.borderGradientStart || "#2d2e83");
  gradient.addColorStop(1, qrCodeSettings.borderGradientEnd || "#2d2e83");

  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, borderCanvas.width, borderCanvas.height);

  const borderThickness = 0.3 * scale;
  const t = borderThickness * (dpi / 2.54);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, borderCanvas.width, t);
  ctx.fillRect(0, borderCanvas.height - t, borderCanvas.width, t);
  ctx.fillRect(0, 0, t, borderCanvas.height);
  ctx.fillRect(borderCanvas.width - t, 0, t, borderCanvas.height);

  const borderDataURL = borderCanvas.toDataURL("image/png");

  const pdf = new jsPDF({ unit: "cm", format: [pageW, pageH] });
  pdf.addImage(
    borderDataURL,
    "PNG",
    safeMargin,
    safeMargin,
    pageW - 2 * safeMargin,
    pageH - 2 * safeMargin
  );

  const centerX = pageW / 2;
  let y = safeMargin + 1 * scale;

  const drawRest = () => {
    // --- Title ---
    if (qrTitle) {
      pdf.setFont("helvetica", "bold");
      let fontSize = (qrTitleSize || 18) * scale;
      fontSize = Math.max(fontSize, 10);
      pdf.setFontSize(fontSize);
      pdf.setTextColor(qrTitleColor || "#2d2e83");

      const maxWidth = pageW - 2.5 * safeMargin;
      const titleLines = pdf.splitTextToSize(qrTitle, maxWidth);
      pdf.text(titleLines, centerX, y, { align: "center" });

      const lineSpacing = fontSize * 0.02;
      const logoMargin = qrCodeSettings.showHolderLogo ? 1.0 * scale : 0.5 * scale;
      y += titleLines.length * (fontSize * 0.04 + lineSpacing) + logoMargin;
    }

    // --- Description ---
    if (qrDesc) {
      pdf.setFont("helvetica", "normal");
      let fontSize = (qrDescSize || 13) * scale;
      fontSize = Math.max(fontSize, 8);
      pdf.setFontSize(fontSize);
      pdf.setTextColor(qrDescColor || "#666");

      const maxWidth = pageW - 2.5 * safeMargin;
      const descLines = pdf.splitTextToSize(qrDesc, maxWidth);
      pdf.text(descLines, centerX, y, { align: "center" });

      const lineSpacing = fontSize * 0.02;
      // ⬆️ reduced margin so description sits higher
      y += descLines.length * (fontSize * 0.04 + lineSpacing) + 0.1 * scale;
    }

    // --- QR Container ---
    const borderWidthCm = 0.132 * scale;
    const paddingCm = 0.2 * scale;
    const radiusCm = 0.317 * scale;
    const availableHeight = pageH - y - footerH - safeMargin;

    let qrContainerCm;
    if (heightIn <= 3) {
      qrContainerCm = Math.min(4.2, availableHeight * 0.55, pageW - 2 * safeMargin);
    } else if (heightIn <= 4) {
      qrContainerCm = Math.min(6.0, availableHeight * 0.9, pageW - 2 * safeMargin);
    } else if (heightIn <= 6) {
      // ⬆️ bigger QR for 4×6
      qrContainerCm = Math.min(7.2, availableHeight * 0.75, pageW - 2 * safeMargin);
    } else {
      qrContainerCm = Math.min(7.5, availableHeight * 0.78, pageW - 2 * safeMargin);
    }

    const innerCm = qrContainerCm - 2 * borderWidthCm;
    const qrCm = innerCm - 2 * paddingCm;

    let scanMeH = 1.2 * scale;
    const footerY = pageH - footerH - safeMargin;
    const minRequired = qrContainerCm + scanMeH + 0.1 * scale;
    const showScanMe = availableHeight >= minRequired;
    if (!showScanMe) scanMeH = 0;

    const qrContainerY = y + (availableHeight - (qrContainerCm + scanMeH)) / 2 - 0.6 * scale;
    const qrContainerX = safeMargin + (pageW - 2 * safeMargin - qrContainerCm) / 2;

    // Outer rect
    pdf.setFillColor(qrTitleColor || "#2d2e83");
    pdf.roundedRect(qrContainerX, qrContainerY, qrContainerCm, qrContainerCm, radiusCm, radiusCm, "F");

    // Inner rect
    pdf.setFillColor("#fff");
    pdf.roundedRect(
      qrContainerX + borderWidthCm,
      qrContainerY + borderWidthCm,
      innerCm,
      innerCm,
      radiusCm - borderWidthCm,
      radiusCm - borderWidthCm,
      "F"
    );

    const qrPixel = Math.round((qrCm / 2.54) * dpi);
    generateQRCanvas((canvas) => {
      const qrDataURL = canvas.toDataURL("image/png");
      pdf.addImage(
        qrDataURL,
        "PNG",
        qrContainerX + borderWidthCm + paddingCm,
        qrContainerY + borderWidthCm + paddingCm,
        qrCm,
        qrCm
      );

      // --- Scan Me ---
      if (showScanMe) {
        const scanY = qrContainerY + qrContainerCm + 0.7 * scale;
        let scanFontSize = pageSize === "4-6" ? 16 : 16 * scale;
        scanFontSize = Math.max(scanFontSize, 10);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(scanFontSize);
        pdf.setTextColor("#060747ff");
        pdf.text("Scan Me", centerX, scanY, { align: "center" });
      }

      // --- Footer Strip ---
      pdf.setFillColor("#2d2e83");
      pdf.rect(safeMargin, footerY, pageW - 2 * safeMargin, footerH, "F");

      // Footer Branding
      pdf.setTextColor("#fff");

      let footerTitleFontSize = Math.min(Math.max(11 * scale, 9), 16);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(footerTitleFontSize);
      pdf.text("Powered by NetShine", centerX, footerY + 1.05 * scale, { align: "center" });

      let footerDetailFontSize = Math.min(Math.max(9 * scale, 8), 14);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(footerDetailFontSize);
      pdf.text(
        "info@netshinetech.com | www.netshineoffice.com",
        centerX,
        footerY + 1.65 * scale,
        { align: "center" }
      );

      pdf.text("Phone: +91 8264706203", centerX, footerY + 2.25 * scale, { align: "center" });

      const fileName = `NetShine-QR-${widthIn}x${heightIn}.pdf`;
      pdf.save(fileName);
    }, qrPixel);
  };

  // --- Header Logo ---
  if (qrCodeSettings.showHolderLogo) {
    const logoWidthCm = (qrCodeSettings.headerLogoWidth || headerLogoWidth || 250) * pxToCm * scale;
    const logoHeightCm = (qrCodeSettings.headerLogoHeight || headerLogoHeight || 150) * pxToCm * scale;

    const headerLogoImg = new Image();
    headerLogoImg.crossOrigin = "anonymous";
    headerLogoImg.src = qrCodeSettings.headerLogo || defaultLogoSrc;

    headerLogoImg.onload = () => {
      pdf.addImage(headerLogoImg, "PNG", (pageW - logoWidthCm) / 2, y, logoWidthCm, logoHeightCm);
      y += logoHeightCm + 1.0 * scale;
      drawRest();
    };

    headerLogoImg.onerror = () => {
      notify("error", "Failed to load header logo");
      drawRest();
    };
  } else {
    drawRest();
  }
};







const downloadQRPNG = (
  qrTitle,
  qrTitleColor,
  qrTitleSize,
  qrDesc,
  qrDescColor,
  qrDescSize,
  headerLogoWidth,
  headerLogoHeight
) => {
  const pageSize = qrCodeSettings.pageSize || "4-6";
  const [widthIn, heightIn] = pageSize.split("-").map(Number);
  const baseWidthIn = 4;
  const baseHeightIn = 6;
  const scale = Math.min(widthIn / baseWidthIn, heightIn / baseHeightIn);
  const pageW = widthIn * 2.54; // cm
  const pageH = heightIn * 2.54; // cm
  const dpi = 300;
  const cmToPx = dpi / 2.54;
  const ptToPx = dpi / 72;
  const pageWPx = Math.round(pageW * cmToPx);
  const pageHPx = Math.round(pageH * cmToPx);
  // Safe margin inside border
  const safeMargin = qrCodeSettings.borderless ? 0 : 0.25; // cm
  // Footer strip height
  const footerH = 2.5 * scale;
  const canvas = document.createElement("canvas");
  canvas.width = pageWPx;
  canvas.height = pageHPx;
  const ctx = canvas.getContext("2d");
  // --- Gradient border background ---
  const gradient = ctx.createLinearGradient(0, 0, pageWPx, pageHPx);
  gradient.addColorStop(0, qrCodeSettings.borderGradientStart || "#2d2e83");
  gradient.addColorStop(1, qrCodeSettings.borderGradientEnd || "#2d2e83");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, pageWPx, pageHPx);
  const borderThickness = 0.3 * scale;
  const t = borderThickness * cmToPx;
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, pageWPx, t);
  ctx.fillRect(0, pageHPx - t, pageWPx, t);
  ctx.fillRect(0, 0, t, pageHPx);
  ctx.fillRect(pageWPx - t, 0, t, pageHPx);
  const centerX = pageW / 2;
  let y = safeMargin + 1 * scale;
  const splitTextToSize = (text, maxWidthCm, fontSizePt, fontWeight = "normal") => {
    const fontSizePx = fontSizePt * ptToPx;
    ctx.font = `${fontWeight} ${fontSizePx}px Helvetica, sans-serif`;
    const maxWidthPx = maxWidthCm * cmToPx;
    const words = text.split(" ");
    const lines = [];
    let currentLine = words[0] || "";
    for (let i = 1; i < words.length; i++) {
      const word = words[i];
      const width = ctx.measureText(currentLine + " " + word).width;
      if (width < maxWidthPx) {
        currentLine += " " + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }
    lines.push(currentLine);
    return lines;
  };
  const drawRoundedRect = (x, y, w, h, r, fillStyle) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.arc(x + w - r, y + r, r, -Math.PI / 2, 0);
    ctx.lineTo(x + w, y + h - r);
    ctx.arc(x + w - r, y + h - r, r, 0, Math.PI / 2);
    ctx.lineTo(x + r, y + h);
    ctx.arc(x + r, y + h - r, r, Math.PI / 2, Math.PI);
    ctx.lineTo(x, y + r);
    ctx.arc(x + r, y + r, r, Math.PI, (3 * Math.PI) / 2);
    ctx.closePath();
    ctx.fillStyle = fillStyle;
    ctx.fill();
  };
  const drawRest = () => {
    // --- Title ---
    if (qrTitle) {
      let fontSize = (qrTitleSize || 18) * scale;
      fontSize = Math.max(fontSize, 10);
      const fontSizePx = fontSize * ptToPx;
      ctx.font = `bold ${fontSizePx}px Helvetica, sans-serif`;
      ctx.fillStyle = qrTitleColor || "#2d2e83";
      ctx.textAlign = "center";
      const maxWidth = pageW - 2.5 * safeMargin;
      const titleLines = splitTextToSize(qrTitle, maxWidth, fontSize, "bold");
      const lineSpacing = fontSize * 0.02;
      const perLineCm = fontSize * 0.04 + lineSpacing;
      const yPx = y * cmToPx;
      for (let i = 0; i < titleLines.length; i++) {
        ctx.fillText(titleLines[i], centerX * cmToPx, yPx + i * (perLineCm * cmToPx));
      }
      const logoMargin = qrCodeSettings.showHolderLogo ? 1.0 * scale : 0.5 * scale;
      y += titleLines.length * perLineCm + logoMargin;
    }
    // --- Description ---
    if (qrDesc) {
      let fontSize = (qrDescSize || 13) * scale;
      fontSize = Math.max(fontSize, 8);
      const fontSizePx = fontSize * ptToPx;
      ctx.font = `normal ${fontSizePx}px Helvetica, sans-serif`;
      ctx.fillStyle = qrDescColor || "#666";
      ctx.textAlign = "center";
      const maxWidth = pageW - 2.5 * safeMargin;
      const descLines = splitTextToSize(qrDesc, maxWidth, fontSize, "normal");
      const lineSpacing = fontSize * 0.02;
      const perLineCm = fontSize * 0.04 + lineSpacing;
      const yPx = y * cmToPx;
      for (let i = 0; i < descLines.length; i++) {
        ctx.fillText(descLines[i], centerX * cmToPx, yPx + i * (perLineCm * cmToPx));
      }
      // ⬆️ reduced margin so description sits higher
      y += descLines.length * perLineCm + 0.1 * scale;
    }
    // --- QR Container ---
    const borderWidthCm = 0.132 * scale;
    const paddingCm = 0.2 * scale;
    const radiusCm = 0.317 * scale;
    const availableHeight = pageH - y - footerH - safeMargin;
    let qrContainerCm;
    if (heightIn <= 3) {
      qrContainerCm = Math.min(4.2, availableHeight * 0.55, pageW - 2 * safeMargin);
    } else if (heightIn <= 4) {
      qrContainerCm = Math.min(6.0, availableHeight * 0.9, pageW - 2 * safeMargin);
    } else if (heightIn <= 6) {
      // ⬆️ bigger QR for 4×6
      qrContainerCm = Math.min(7.2, availableHeight * 0.75, pageW - 2 * safeMargin);
    } else {
      qrContainerCm = Math.min(7.5, availableHeight * 0.78, pageW - 2 * safeMargin);
    }
    const innerCm = qrContainerCm - 2 * borderWidthCm;
    const qrCm = innerCm - 2 * paddingCm;
    let scanMeH = 1.2 * scale;
    const footerY = pageH - footerH - safeMargin;
    const minRequired = qrContainerCm + scanMeH + 0.1 * scale;
    const showScanMe = availableHeight >= minRequired;
    if (!showScanMe) scanMeH = 0;
    const qrContainerY = y + (availableHeight - (qrContainerCm + scanMeH)) / 2 - 0.6 * scale;
    const qrContainerX = safeMargin + (pageW - 2 * safeMargin - qrContainerCm) / 2;
    // Outer rect
    drawRoundedRect(
      qrContainerX * cmToPx,
      qrContainerY * cmToPx,
      qrContainerCm * cmToPx,
      qrContainerCm * cmToPx,
      radiusCm * cmToPx,
      qrTitleColor || "#2d2e83"
    );
    // Inner rect
    drawRoundedRect(
      (qrContainerX + borderWidthCm) * cmToPx,
      (qrContainerY + borderWidthCm) * cmToPx,
      innerCm * cmToPx,
      innerCm * cmToPx,
      (radiusCm - borderWidthCm) * cmToPx,
      "#fff"
    );
    const qrPixel = Math.round((qrCm / 2.54) * dpi);
    generateQRCanvas((qrCanvas) => {
      const qrDataURL = qrCanvas.toDataURL("image/png");
      const qrImg = new Image();
      qrImg.src = qrDataURL;
      qrImg.onload = () => {
        ctx.drawImage(
          qrImg,
          (qrContainerX + borderWidthCm + paddingCm) * cmToPx,
          (qrContainerY + borderWidthCm + paddingCm) * cmToPx,
          qrCm * cmToPx,
          qrCm * cmToPx
        );
        // --- Scan Me ---
        if (showScanMe) {
          const scanY = qrContainerY + qrContainerCm + 0.7 * scale;
          let scanFontSize = pageSize === "4-6" ? 16 : 16 * scale;
          scanFontSize = Math.max(scanFontSize, 10);
          const scanFontSizePx = scanFontSize * ptToPx;
          ctx.font = `bold ${scanFontSizePx}px Helvetica, sans-serif`;
          ctx.fillStyle = "#060747ff";
          ctx.textAlign = "center";
          ctx.fillText("Scan Me", centerX * cmToPx, scanY * cmToPx);
        }
        // --- Footer Strip ---
        ctx.fillStyle = "#2d2e83";
        ctx.fillRect(safeMargin * cmToPx, footerY * cmToPx, (pageW - 2 * safeMargin) * cmToPx, footerH * cmToPx);
        // Footer Branding
        ctx.fillStyle = "#fff";
        ctx.textAlign = "center";
        let footerTitleFontSize = Math.min(Math.max(11 * scale, 9), 16);
        const footerTitleFontSizePx = footerTitleFontSize * ptToPx;
        ctx.font = `bold ${footerTitleFontSizePx}px Helvetica, sans-serif`;
        ctx.fillText("Powered by NetShine", centerX * cmToPx, (footerY + 1.05 * scale) * cmToPx);
        let footerDetailFontSize = Math.min(Math.max(9 * scale, 8), 14);
        const footerDetailFontSizePx = footerDetailFontSize * ptToPx;
        ctx.font = `normal ${footerDetailFontSizePx}px Helvetica, sans-serif`;
        ctx.fillText(
          "info@netshinetech.com | www.netshineoffice.com",
          centerX * cmToPx,
          (footerY + 1.65 * scale) * cmToPx
        );
        ctx.fillText("📞 +91 8264706203", centerX * cmToPx, (footerY + 2.25 * scale) * cmToPx);
        // ✅ Save file with page size suffix
        const fileName = `NetShine-QR-${widthIn}x${heightIn}.png`;
        const link = document.createElement("a");
        link.download = fileName;
        link.href = canvas.toDataURL("image/png");
        link.click();
      };
    }, qrPixel);
  };
  // --- Header Logo ---
  if (qrCodeSettings.showHolderLogo) {
    const logoWidthCm = (qrCodeSettings.headerLogoWidth || headerLogoWidth || 250) * (2.54 / 96) * scale;
    const logoHeightCm = (qrCodeSettings.headerLogoHeight || headerLogoHeight || 150) * (2.54 / 96) * scale;
    const headerLogoImg = new Image();
    headerLogoImg.crossOrigin = "anonymous";
    headerLogoImg.src = qrCodeSettings.headerLogo || defaultLogoSrc;
    headerLogoImg.onload = () => {
      ctx.drawImage(headerLogoImg, ((pageW - logoWidthCm) / 2) * cmToPx, y * cmToPx, logoWidthCm * cmToPx, logoHeightCm * cmToPx);
      y += logoHeightCm + 1.0 * scale;
      drawRest();
    };
    headerLogoImg.onerror = () => {
      notify("error", "Failed to load header logo");
      drawRest();
    };
  } else {
    drawRest();
  }
};
  const saveSurvey = async () => {
    const payload = {
      title: editingSurvey?.title || "",
      description: editingSurvey?.description || "",
      status: editingSurvey?.status || "Draft",
      questions: questions || [],
      branding: branding || {},
      settings: settings || {},
      qrcode_settings: qrCodeSettings || {},
      conditional_logic: conditionalLogic || {}, // ✅ always here now
      sharing: { key: editingSurvey?.key || `survey-${Date.now()}` },
    };
    try {
      setLoading(true);
      let isNew = !editingSurvey?.surveyid;
      if (!isNew) {
        await axios.put(`${apiUrl}/feedback/surveys/${editingSurvey.surveyid}`, payload, authHeaders());
        notify("success", "Survey updated successfully.");
      } else {
        const res = await axios.post(`${apiUrl}/feedback/surveys`, payload, authHeaders());
        if (res.data) {
          notify("success", "Survey created successfully.");
          setEditingSurvey({ ...editingSurvey, surveyid: res.data.surveyid });
          updateSharing({ ...editingSurvey, surveyid: res.data.surveyid });
        }
      }
      await fetchSurveys();
      initialConfigRef.current = JSON.stringify(payload);
      if (isNew) stopEditing(); // Land to main page after creation
    } catch (err) {
      console.error("Error saving survey:", err);
      notify("error", "Failed to save survey.");
    } finally {
      setLoading(false);
    }
  };
  const confirmActionHandler = (action, data) => {
    setConfirmAction(action);
    setConfirmData(data);
    setShowConfirmModal(true);
  };
  const handleConfirm = async () => {
    if (confirmAction === "delete") {
      await deleteSurvey(confirmData.id || confirmData.surveyid);
      notify("success", "Survey deleted successfully.");
    } else if (confirmAction === "duplicate") {
      await duplicateSurvey(confirmData);
      notify("success", "Survey duplicated successfully.");
    }
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };
  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };
  const deleteSurvey = async (id) => {
    try {
      setLoading(true);
      const survey = surveys.find((s) => s.id === id || s.surveyid === id);
      const surveyid = survey?.surveyid || id;
      await axios.delete(`${apiUrl}/feedback/surveys/${surveyid}`, authHeaders());
      setSurveys((prev) => prev.filter((s) => s.surveyid !== surveyid && s.id !== id));
    } catch (err) {
      console.error("Error deleting survey:", err);
      notify("error", "Failed to delete survey.");
    } finally {
      setLoading(false);
    }
  };
  const duplicateSurvey = async (survey) => {
    try {
      setLoading(true);
      const payload = {
        title: `${survey.title} (Copy)`,
        description: survey.description || "",
        status: "Draft",
        questions: survey.questions || [],
        branding: survey.branding || {},
        settings: survey.settings || {},
        qrcode_settings: survey.qrCodeSettings || qrCodeSettings,
        conditional_logic: survey.conditionalLogic || conditionalLogic,
        sharing: { key: `${survey.key || "copy"}-copy-${Date.now()}` },
      };
      await axios.post(`${apiUrl}/feedback/surveys`, payload, authHeaders());
      await fetchSurveys();
    } catch (err) {
      console.error("Error duplicating survey:", err);
      notify("error", "Failed to duplicate survey.");
    } finally {
      setLoading(false);
    }
  };
  const addQuestion = (type) => {
    let newQuestion = { id: questions.length + 1, type, label: "", required: false, placeholder: "" };
    if (type === "rating") newQuestion.style = "star";
    setQuestions([...questions, newQuestion]);
  };
  const updateQuestion = (id, field, value) => {
    setQuestions(questions.map((q) => (q.id === id ? { ...q, [field]: value } : q)));
  };
  const removeQuestion = (id) => {
    setQuestions(questions.filter((q) => q.id !== id));
  };
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(questions);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setQuestions(reordered);
  };
  const renderQuestionPreview = (q) => {
    switch (q.type) {
      case "rating":
        return q.style === "star" ? (
          <div className="star-rating-preview">⭐ ⭐ ⭐ ⭐ ⭐</div>
        ) : (
          <div className="smiley-rating-preview">😞 😟 😐 🙂 😄</div>
        );
      case "nps":
        return (
          <div
            className="nps-preview"
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "10px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              background: "#f9f9f9",
            }}
          >
            {[...Array(11)].map((_, i) => (
              <button
                key={i}
                style={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                  background: i === 5 ? branding.themeColor : "#fff",
                  color: i === 5 ? "#fff" : "#000",
                }}
              >
                {i}
              </button>
            ))}
          </div>
        );
      case "text":
        return <Form.Control as="textarea" placeholder={q.placeholder} className="form-input" disabled />;
      case "multiple":
        return (
          <Form.Select className="form-input" disabled>
            <option>Option 1</option>
          </Form.Select>
        );
      case "checkbox":
        return <Form.Check type="checkbox" label="Option" className="form-checkbox" disabled />;
      case "date":
        return <Form.Control type="date" className="form-input" disabled />;
      default:
        return null;
    }
  };
  const handleLogoUrlChange = (e) => setBranding({ ...branding, logoSrc: e.target.value });
  const handleBackgroundChange = (e) => setBranding({ ...branding, background: e.target.value });
  const handleBackgroundValueChange = (value) => setBranding({ ...branding, backgroundValue: value });
  const getBackgroundStyle = () =>
    branding.background === "gradient"
      ? "linear-gradient(to right, #f0f0f0, #ffffff)"
      : branding.background === "image"
      ? `url(${branding.backgroundValue})`
      : branding.backgroundValue;
  const handleColorChange = (field, color) => setBranding({ ...branding, [field]: color.hex });
  const handleTitleFontSizeChange = (e) => setBranding({ ...branding, titleFontSize: parseFloat(e.target.value) });
  const handleConditionalChange = (field, value) =>
    setConditionalLogic({
      ...conditionalLogic,
      [field]: value,
    });
  const handleQRSettingChange = (field, value) => {
    setQrCodeSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const saveGlobalSettings = async () => {
    try {
      setLoading(true);
      await axios.put(`${apiUrl}/feedback/settings`, globalSettings, authHeaders());
      notify("success", "Global settings saved successfully.");
    } catch (err) {
      console.error("Error saving global settings:", err);
      notify("error", "Failed to save global settings.");
    } finally {
      setLoading(false);
    }
  };
  const handleUsePreviewTemplate = (template) => {
    loadTemplate(template);
    setPreviewTemplate(null);
  };
  const isSaved = JSON.stringify({ questions, branding, settings, qrCodeSettings }) === initialConfigRef.current;
  return (
    <div className="page-wrapper-full">
      <div className="content container-fluid">
        <div className="page-header page-top-icons" aria-label="Page Header">
          <div className="row align-items-center">
            <div className="col-md-12 d-flex justify-content-between align-items-center">
              <h3 className="fw-bold text-dark mb-0">
                <i className="fas fa-poll text-primary me-2" aria-hidden="true"></i> Manage Feedback
              </h3>
              <div className="d-flex gap-2">
                {editingSurvey && (
                  <Button
                    variant="secondary"
                    onClick={stopEditing}
                    className="px-4 py-2"
                    aria-label="Cancel Editing"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={() => (editingSurvey ? saveSurvey() : startEditing())}
                  className="px-4 py-2"
                  aria-label={editingSurvey ? "Save Survey" : "Create New Survey"}
                  disabled={loading}
                >
                  {editingSurvey ? "Save" : "+ Create New Survey"}
                </Button>
              </div>
            </div>
          </div>
        </div>
        {!editingSurvey ? (
          loading ? (
            <div>Loading surveys...</div>
          ) : Array.isArray(surveys) ? (
            <SurveyList
              surveys={surveys}
              startEditing={startEditing}
              confirmActionHandler={confirmActionHandler}
              loading={loading}
            />
          ) : (
            <div>No surveys available</div>
          )
        ) : (
          <SurveyEditor
            editingSurvey={editingSurvey}
            setEditingSurvey={setEditingSurvey}
            questions={questions}
            setQuestions={setQuestions}
            branding={branding}
            setBranding={setBranding}
            settings={settings}
            setSettings={setSettings}
            globalSettings={globalSettings}
            setGlobalSettings={setGlobalSettings}
            qrCodeSettings={qrCodeSettings}
            setQrCodeSettings={setQrCodeSettings}
            conditionalLogic={conditionalLogic}
            setConditionalLogic={setConditionalLogic}
            surveyLink={surveyLink}
            saveSurvey={saveSurvey}
            stopEditing={stopEditing}
            fetchGlobalSettings={fetchGlobalSettings}
            saveGlobalSettings={saveGlobalSettings}
            addQuestion={addQuestion}
            updateQuestion={updateQuestion}
            removeQuestion={removeQuestion}
            onDragEnd={onDragEnd}
            renderQuestionPreview={renderQuestionPreview}
            handleLogoUrlChange={handleLogoUrlChange}
            handleBackgroundChange={handleBackgroundChange}
            handleBackgroundValueChange={handleBackgroundValueChange}
            handleColorChange={handleColorChange}
            handleTitleFontSizeChange={handleTitleFontSizeChange}
            handleConditionalChange={handleConditionalChange}
            handleQRSettingChange={handleQRSettingChange}
            previewDevice={previewDevice}
            setPreviewDevice={setPreviewDevice}
            getBackgroundStyle={getBackgroundStyle}
            isDownloadOpen={isDownloadOpen}
            setIsDownloadOpen={setIsDownloadOpen}
            downloadQRPDF={downloadQRPDF}
            downloadQRPNG={downloadQRPNG}
            generateQRCanvas={generateQRCanvas}
            activeKey={activeKey}
            onSelect={(key) => setActiveKey(key)}
          />
        )}
        <Modal
          show={showConfirmModal}
          onHide={handleCancelConfirm}
          centered
          aria-label="Confirmation Modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirm Action</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {confirmAction === "delete" && (
              <p>Are you sure you want to delete the survey "{confirmData?.title}"?</p>
            )}
            {confirmAction === "duplicate" && (
              <p>Are you sure you want to duplicate the survey "{confirmData?.title}"?</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCancelConfirm}>
              No
            </Button>
            <Button variant="primary" onClick={handleConfirm}>
              Yes
            </Button>
          </Modal.Footer>
        </Modal>
        <Modal
          show={showTemplateModal}
          onHide={() => setShowTemplateModal(false)}
          size="xl"
          dialogClassName="modal-right"
          style={{ height: "100vh", margin: 0 }}
          aria-label="Template Selection Modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Select a Template</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4 d-flex flex-column" style={{ height: "calc(100vh - 60px)" }}>
            <div className="template-grid flex-grow-1 overflow-auto">
              {templates.map((template, index) => (
                <Card
                  key={index}
                  className="shadow-sm template-card border-0"
                  style={{ borderRadius: "12px", cursor: "pointer" }}
                  onClick={() => loadTemplate(template)}
                  aria-label={`Select ${template.name} Template`}
                >
                  <Card.Body className="p-3 d-flex flex-column justify-content-between">
                    <div>
                      <h6 className="fw-bold text-dark mb-2">{template.name}</h6>
                      <p className="text-muted small mb-3">{template.description}</p>
                    </div>
                    <div className="d-flex justify-content-between">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => setPreviewTemplate(template)}
                        aria-label={`Preview ${template.name}`}
                      >
                        <i className="fas fa-eye me-1"></i> Preview
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => loadTemplate(template)}
                        aria-label={`Use ${template.name}`}
                      >
                        <i className="fas fa-plus me-1"></i> Use
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ))}
              <Card
                className="shadow-sm template-blank-card text-center d-flex align-items-center justify-content-center"
                style={{ borderRadius: "12px", border: "2px dashed #007bff", background: "#f8faff", cursor: "pointer" }}
                onClick={loadBlank}
                aria-label="Start Blank Template"
              >
                <Card.Body>
                  <h6 className="fw-bold text-primary mb-2">Start Blank</h6>
                  <p className="text-muted small mb-2">Create a survey from scratch with no predefined settings.</p>
                  <i className="fas fa-plus-circle fa-2x text-primary"></i>
                </Card.Body>
              </Card>
            </div>
          </Modal.Body>
        </Modal>
        <Modal
          show={!!previewTemplate}
          onHide={() => setPreviewTemplate(null)}
          size="lg"
          centered
          aria-label="Template Preview Modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>{previewTemplate?.name} - Preview</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <h5 className="mb-3">{previewTemplate?.title}</h5>
            {previewTemplate?.questions?.map((q, idx) => (
              <div key={idx} className="mb-3">
                <Form.Label>
                  {q.label} {q.required && <span className="text-danger">*</span>}
                </Form.Label>
                {q.type === "text" && <Form.Control placeholder={q.placeholder} disabled />}
                {q.type === "rating" && <div>⭐ ⭐ ⭐ ⭐ ⭐</div>}
                {q.type === "nps" && <div>[0 - 10 NPS Scale]</div>}
                {q.type === "multiple" && (
                  <Form.Select disabled>
                    <option>Option 1</option>
                  </Form.Select>
                )}
                {q.type === "checkbox" && <Form.Check label="Sample Option" disabled />}
                {q.type === "date" && <Form.Control type="date" disabled />}
              </div>
            ))}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setPreviewTemplate(null)}>
              Close
            </Button>
            <Button variant="primary" onClick={() => handleUsePreviewTemplate(previewTemplate)}>
              Use This Template
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
      <style>{`
        .survey-card {
          transition: all 0.25s ease;
        }
        .survey-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
          border-left: 4px solid var(--bs-primary);
        }
        .template-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
          border-left: 4px solid #007bff;
        }
        .template-blank-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
          background: #eef6ff;
        }
        .template-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap: 1.5rem;
        }
        .template-card,
        .template-blank-card {
          border-radius: 12px;
          transition: all 0.3s ease;
          cursor: pointer;
          background: #fff;
        }
        .modal-right .modal-dialog {
          position: fixed;
          top: 0;
          right: 0;
          margin: 0;
          height: 100vh;
          transform: none !important;
        }
        .modal-right .modal-content {
          height: 100%;
          border-radius: 0;
        }
        .modal-right .modal-body {
          overflow-y: auto;
        }
        .survey-card .btn-link {
          padding: 0.25rem 0.5rem;
          font-size: 1.1rem;
        }
        .survey-card .btn-link:hover {
          text-decoration: none;ss
          transform: scale(1.15);
          transition: transform 0.15s ease;
        }
        .survey-card .card-footer {
          border-top: 1px solid #f0f0f0;
        }
      `}</style>
    </div>
  );
};

export default ManageSurveys;